import { z } from "zod";

// Piece jointe schema
const pieceJointeSchema = z.object({
  titre: z.string(),
  url: z.string(),
});

// Outil lié schema
const outilLieSchema = z.object({
  titre: z.string(),
  url: z.string(),
});

// Article reference (Legifrance / recodification) — used in unified Source.refs and legacy refLegifrance
export const articleRefSchema = z.object({
  nouveau: z.string(),
  ancien: z.string().nullable().optional(),
});

// ----- New unified format (single flat sources array) -----
export const sourceTypeSchema = z.enum([
  "sidoni",
  "legifrance",
  "anc",
  "h2a",
  "macncc",
  "cncc",
  "formation",
]);

/**
 * Unified source shape after validation. Accepts common LLM variants:
 * - omits id/nom/refs (filled with null / inferred)
 * - Légifrance: `article` + `ancien` instead of `refs`
 * - `extrait` as fallback for `resume`
 * - formation / CNCC: `titre` when `nom` is missing
 * - `id` as string (coerced to number when possible)
 */
export const sourceSchema = z
  .object({
    type: sourceTypeSchema,
    id: z.union([z.number(), z.string(), z.null()]).optional(),
    nom: z.string().nullable().optional(),
    titre: z.string().optional(),
    url: z.string().nullable().optional(),
    resume: z.string().optional(),
    refs: z.array(articleRefSchema).nullable().optional(),
    article: z.string().optional(),
    ancien: z.string().nullable().optional(),
    extrait: z.string().optional(),
  })
  .transform((s) => {
    const id = (() => {
      const v = s.id;
      if (v == null) return null;
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      }
      return null;
    })();

    const refsFromArticle =
      s.article != null && String(s.article).length > 0
        ? [{ nouveau: s.article, ancien: s.ancien ?? null }]
        : null;

    return {
      type: s.type,
      id,
      nom: s.nom ?? s.titre ?? null,
      url: s.url ?? null,
      resume: s.resume ?? s.extrait ?? "",
      refs: s.refs ?? refsFromArticle ?? null,
    };
  });

export const flagsSchema = z.object({
  recodification: z.boolean(),
  archive_detected: z.boolean(),
});

export const apiResponseSchema = z.object({
  sources: z.array(sourceSchema),
  reponse: z.string(),
  flags: flagsSchema.optional(),
});

// ----- Legacy per-source schemas (deprecated, for backward compatibility) -----

// Sidoni source schema
// Accepts both the new slim format (id, nom, resume) from Dust AI
// and the legacy full format (titre, url, dateMiseAJour, ...) for backward compatibility.
// Missing fields are enriched at runtime via the /sidoni-documents/by-ids endpoint.
export const sidoniSourceSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  nom: z.string().optional(),
  titre: z.string().optional(),
  datePublication: z.string().optional(),
  dateMiseAJour: z.string().optional(),
  url: z.string().optional(),
  avertissementDocument: z.string().optional(),
  resume: z.string().optional(),
  statut: z.string().optional(),
  typeDocument: z.string().optional(),
  classeDocument: z.string().optional(),
  diffusion: z.string().optional(),
  refLegifrance: z.array(articleRefSchema).optional(),
  piecesJointes: z.array(pieceJointeSchema).optional(),
  outilsLies: z.array(outilLieSchema).optional(),
});

// Legifrance source schema
export const legifranceSourceSchema = z.object({
  titre: z.string(),
  url: z.string(),
  reference: z.string().optional(),
});

// ANC source schema
export const ancSourceSchema = z.object({
  titre: z.string(),
  url: z.string(),
  typeDocument: z.string().optional(),
});

// H2A source schema
export const h2aSourceSchema = z.object({
  titre: z.string(),
  url: z.string(),
  typeDocument: z.string().optional(),
});

// MaCNCC source schema
export const maCNCCSourceSchema = z.object({
  titre: z.string(),
  url: z.string(),
  typeOutil: z.string().optional(),
});

// Autres CNCC source schema
export const autresCNCCSourceSchema = z.object({
  titre: z.string(),
  url: z.string(),
  typeRessource: z.string().optional(),
  siteCNCC: z.string().optional(),
});

// Structured response schema (matching dust_json_schema.json) — LEGACY
export const structuredResponseSchema = z.object({
  avertissement: z.string().optional(),
  sourceSidoni: z.array(sidoniSourceSchema),
  sourceLegifrance: z.array(legifranceSourceSchema).optional(),
  sourceANC: z.array(ancSourceSchema).optional(),
  sourceH2A: z.array(h2aSourceSchema).optional(),
  sourceMaCNCC: z.array(maCNCCSourceSchema).optional(),
  autresSourcesCNCC: z.array(autresCNCCSourceSchema).optional(),
  suggestionReponse: z.string(),
});

// Enriched Sidoni document returned by the /sidoni-documents/by-ids endpoint
export const enrichedSidoniDocumentSchema = z.object({
  id: z.number(),
  nom: z.string(),
  titre: z.string(),
  datePublication: z.string(),
  dateMiseAJour: z.string(),
  url: z.string(),
  avertissementDocument: z.string().nullable(),
  statut: z.string().nullable(),
  typeDocument: z.string().nullable(),
  classeDocument: z.string().nullable(),
  diffusion: z.string().nullable(),
  piecesJointes: z.array(pieceJointeSchema),
  outilsLies: z.array(outilLieSchema),
});

// Inferred types — new unified format
export type ArticleRef = z.infer<typeof articleRefSchema>;
export type SourceType = z.infer<typeof sourceTypeSchema>;
export type Source = z.infer<typeof sourceSchema>;
export type Flags = z.infer<typeof flagsSchema>;
export type ApiResponse = z.infer<typeof apiResponseSchema>;

// Inferred types — legacy
export type SidoniSource = z.infer<typeof sidoniSourceSchema>;
export type EnrichedSidoniDocument = z.infer<
  typeof enrichedSidoniDocumentSchema
>;
export type LegifranceSource = z.infer<typeof legifranceSourceSchema>;
export type ANCSource = z.infer<typeof ancSourceSchema>;
export type H2ASource = z.infer<typeof h2aSourceSchema>;
export type MaCNCCSource = z.infer<typeof maCNCCSourceSchema>;
export type AutresCNCCSource = z.infer<typeof autresCNCCSourceSchema>;
/** @deprecated Use ApiResponse for new code. Kept for parsing/backward compat. */
export type StructuredResponse = z.infer<typeof structuredResponseSchema>;
